import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { client } from '../amplify-client';
import { useRolle } from '../auth/useRolle';
import { kalendertagBerlin } from '../../amplify/shared/zeit';
import { GuennieRating } from '../components/GuennieRating';
import { KriteriumZeile } from '../components/KriteriumZeile';
import { SpaghettiDivider } from '../components/SpaghettiDivider';
import { AdressSuche } from '../components/AdressSuche';
import { MiniKarte } from '../components/MiniKarte';
import { bildVerkleinern } from '../lib/bildVerkleinern';
import { findeMoeglicheDublette, type EisdielePosition } from '../lib/dublettenCheck';
import { erzeugeId } from '../lib/id';
import { uebersetzeSchreibFehler } from '../lib/fehler';
import { PFLICHT_KRITERIEN, OPTIONALE_KRITERIEN, type Kriterium } from '../lib/kriterien';
import {
  berechneGesamtGuennies,
  pflichtnotenVollstaendig,
} from '../../amplify/shared/guennies';

type EisdieleKurz = EisdielePosition;

type NeueEisdiele = {
  name: string;
  strasse: string;
  plz: string;
  ort: string;
  region: string;
  website: string;
  lat: number | null;
  lng: number | null;
};

const LEERE_EISDIELE: NeueEisdiele = {
  name: '',
  strasse: '',
  plz: '',
  ort: '',
  region: '',
  website: '',
  lat: null,
  lng: null,
};

const HEUTE = new Date().toISOString().slice(0, 10);

export function NeueVerkostung() {
  const navigate = useNavigate();
  const { sub, istVerwalter } = useRolle();

  const [eisdielen, setEisdielen] = useState<EisdieleKurz[]>([]);
  const [eisdieleId, setEisdieleId] = useState('');
  const [neueEisdiele, setNeueEisdiele] = useState(false);
  const [neu, setNeu] = useState<NeueEisdiele>(LEERE_EISDIELE);
  const [dublette, setDublette] = useState<EisdielePosition | null>(null);

  const [besuchsdatum, setBesuchsdatum] = useState(HEUTE);
  const [variante, setVariante] = useState('');
  const [preis, setPreis] = useState('');
  const [serviertAls, setServiertAls] = useState('');
  const [wartezeit, setWartezeit] = useState('');
  const [noten, setNoten] = useState<Partial<Record<Kriterium, number>>>({});
  const [fazit, setFazit] = useState('');
  const [wiederkommen, setWiederkommen] = useState('');
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoVorschau, setFotoVorschau] = useState<string | null>(null);
  const kameraInputRef = useRef<HTMLInputElement>(null);
  const dateiInputRef = useRef<HTMLInputElement>(null);

  const [restKontingent, setRestKontingent] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    client.models.Eisdiele.list({ selectionSet: ['id', 'name', 'ort', 'lat', 'lng'], limit: 1000 }).then(
      ({ data }) =>
        setEisdielen(
          (data ?? [])
            .map((e) => ({ id: e.id, name: e.name, ort: e.ort, lat: e.lat, lng: e.lng }))
            .sort((a, b) => a.name.localeCompare(b.name, 'de')),
        ),
    );
  }, []);

  // Dubletten-Hinweis: bei jeder Positionsänderung der neuen Eisdiele prüfen
  useEffect(() => {
    if (neu.lat == null || neu.lng == null) {
      setDublette(null);
      return;
    }
    setDublette(findeMoeglicheDublette(neu.lat, neu.lng, eisdielen));
  }, [neu.lat, neu.lng, eisdielen]);

  // Tageskontingent (informativ): eigene Verkostungen von heute zählen
  useEffect(() => {
    if (!sub || istVerwalter) {
      setRestKontingent(null);
      return;
    }
    client.models.SpaghettiEisTest.verkostungenNachErstellerUndTag(
      { erstelltVon: sub, erstelltAmTag: { eq: kalendertagBerlin() } },
      { selectionSet: ['id'], limit: 20 },
    )
      .then(({ data }) => setRestKontingent(Math.max(0, 5 - (data?.length ?? 0))))
      .catch(() => setRestKontingent(null));
  }, [sub, istVerwalter]);

  const gesamt = useMemo(() => berechneGesamtGuennies(noten), [noten]);
  const pflichtOk = pflichtnotenVollstaendig(noten);

  function setzeNote(k: Kriterium, wert: number) {
    setNoten((n) => ({ ...n, [k]: wert }));
  }

  async function fotoGewaehlt(datei: File | null) {
    setFehler(null);
    if (!datei) {
      setFoto(null);
      setFotoVorschau(null);
      return;
    }
    try {
      const blob = await bildVerkleinern(datei);
      const f = new File([blob], 'foto.jpg', { type: 'image/jpeg' });
      setFoto(f);
      setFotoVorschau(URL.createObjectURL(f));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  async function eisdieleSicherstellen(): Promise<string> {
    if (!neueEisdiele) return eisdieleId;
    if (!neu.name || !neu.strasse || !neu.plz || !neu.ort || neu.lat == null || neu.lng == null) {
      throw new Error('Für die neue Eisdiele fehlen Name, Adresse oder Position.');
    }
    const { data, errors } = await client.models.Eisdiele.create({
      name: neu.name,
      strasse: neu.strasse,
      plz: neu.plz,
      ort: neu.ort,
      region: neu.region || undefined,
      lat: neu.lat,
      lng: neu.lng,
      website: neu.website || undefined,
    });
    if (errors?.length || !data) throw new Error('Eisdiele konnte nicht angelegt werden.');
    return data.id;
  }

  async function absenden(direktVeroeffentlichen: boolean) {
    setBusy(true);
    setFehler(null);
    try {
      if (direktVeroeffentlichen && !pflichtOk) {
        throw new Error('Für die Veröffentlichung müssen die vier Pflichtkriterien bewertet sein.');
      }
      const zielEisdiele = await eisdieleSicherstellen();

      // Erst die ID erzeugen und – falls ein Foto gewählt wurde – DAMIT eine
      // präsignierte Upload-URL anfordern und hochladen. Die Lambda stellt
      // die URL aus, bevor der Datensatz überhaupt existiert, weil der
      // aufrufende Tester/Verwalter dabei ist, genau diese Verkostung gerade
      // anzulegen (siehe amplify/functions/verkostung-schreiben, Mutation
      // `fotoUploadUrlAnfordern` — Security-Review Fund 1).
      const neueId = erzeugeId();
      let fotoKey: string | undefined;
      if (foto) {
        const { data: uploadUrl, errors: urlErrors } = await client.mutations.fotoUploadUrlAnfordern({
          testId: neueId,
          contentType: 'image/jpeg',
        });
        if (urlErrors?.length || !uploadUrl) {
          throw new Error(urlErrors?.[0]?.message ?? 'Konnte keine Upload-Adresse für das Foto anfordern.');
        }
        const hochladen = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/jpeg' },
          body: foto,
        });
        if (!hochladen.ok) throw new Error('Foto-Upload fehlgeschlagen.');
        fotoKey = `tests/${neueId}/foto.jpg`;
      }

      const { data, errors } = await client.mutations.eisTestAnlegen({
        id: neueId,
        eisdieleId: zielEisdiele,
        besuchsdatum,
        variante: variante || undefined,
        portionspreisEuro: preis ? Number(preis) : undefined,
        serviertAls: serviertAls || undefined,
        wartezeitMin: wartezeit ? Number(wartezeit) : undefined,
        noteSpaghettiOptik: noten.noteSpaghettiOptik!,
        noteKonsistenz: noten.noteKonsistenz!,
        noteGeschmack: noten.noteGeschmack!,
        noteFruchtsauce: noten.noteFruchtsauce!,
        noteWeisseRaspel: noten.noteWeisseRaspel,
        noteSahne: noten.noteSahne,
        notePortionsgroesse: noten.notePortionsgroesse,
        notePraesentation: noten.notePraesentation,
        notePreisLeistung: noten.notePreisLeistung,
        fotoKey,
        fazit: fazit || undefined,
        wiederkommen: wiederkommen || undefined,
        direktVeroeffentlichen,
      });
      if (errors?.length || !data) throw new Error(errors?.[0]?.message ?? 'Speichern fehlgeschlagen.');

      navigate('/app');
    } catch (e) {
      const nachricht = e instanceof Error ? e.message : String(e);
      setFehler(uebersetzeSchreibFehler(nachricht));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container" style={{ maxWidth: '46rem' }}>
      <p className="label">Neue Verkostung</p>
      <h1>Was hast du gegessen?</h1>
      {restKontingent !== null ? (
        <p className="hinweis">Heute noch {restKontingent} von 5 Verkostungen.</p>
      ) : null}
      <SpaghettiDivider />

      {/* Schritt 1: Eisdiele */}
      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <h2>1 · Eisdiele</h2>
        {!neueEisdiele ? (
          <>
            <label htmlFor="eisdiele">Bestehende Eisdiele</label>
            <select id="eisdiele" value={eisdieleId} onChange={(e) => setEisdieleId(e.target.value)}>
              <option value="">– bitte wählen –</option>
              {eisdielen.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.ort}
                </option>
              ))}
            </select>
            <button className="btn btn-sekundaer" style={{ marginTop: '0.75rem' }} onClick={() => setNeueEisdiele(true)}>
              Neue Eisdiele anlegen
            </button>
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
            <AdressSuche
              onAuswahl={(t) =>
                setNeu((n) => ({
                  ...n,
                  strasse: t.strasse || n.strasse,
                  plz: t.plz || n.plz,
                  ort: t.ort || n.ort,
                  region: t.region || n.region,
                  lat: t.lat,
                  lng: t.lng,
                }))
              }
            />
            <div>
              <label>Name</label>
              <input value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
            </div>
            <div>
              <label>Straße & Hausnummer</label>
              <input value={neu.strasse} onChange={(e) => setNeu({ ...neu, strasse: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '0 0 8rem' }}>
                <label>PLZ</label>
                <input value={neu.plz} onChange={(e) => setNeu({ ...neu, plz: e.target.value })} />
              </div>
              <div style={{ flex: '1 1 10rem' }}>
                <label>Ort</label>
                <input value={neu.ort} onChange={(e) => setNeu({ ...neu, ort: e.target.value })} />
              </div>
            </div>

            {neu.lat != null && neu.lng != null ? (
              <MiniKarte
                lat={neu.lat}
                lng={neu.lng}
                onChange={(lat, lng) => setNeu((n) => ({ ...n, lat, lng }))}
              />
            ) : (
              <p className="hinweis">Über die Adresssuche oben eine Adresse wählen, dann erscheint hier die Karte.</p>
            )}

            {dublette ? (
              <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>
                Ganz in der Nähe (≤ 120 m) gibt es schon <strong>{dublette.name}</strong> ({dublette.ort}).
                Vielleicht ist das dieselbe Eisdiele? Dann lieber oben „Bestehende Eisdiele" wählen.
              </p>
            ) : null}

            <div>
              <label>Website (optional)</label>
              <input value={neu.website} onChange={(e) => setNeu({ ...neu, website: e.target.value })} />
            </div>
            <button
              className="btn btn-sekundaer"
              onClick={() => {
                setNeueEisdiele(false);
                setNeu(LEERE_EISDIELE);
              }}
              type="button"
            >
              Zurück zur Auswahl
            </button>
          </div>
        )}
      </section>

      {/* Schritt 2: Bewertung */}
      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <h2>2 · Bewertung</h2>
          <div style={{ textAlign: 'right' }}>
            <div className="label">Gesamt (automatisch)</div>
            <GuennieRating wert={gesamt} groesse="md" />
          </div>
        </div>

        {PFLICHT_KRITERIEN.map((k) => (
          <KriteriumZeile key={k} k={k} wert={noten[k]} onChange={(v) => setzeNote(k, v)} pflicht />
        ))}

        <details style={{ marginTop: '1rem' }}>
          <summary className="label" style={{ cursor: 'pointer' }}>
            Optionale Kriterien
          </summary>
          <div style={{ marginTop: '0.75rem' }}>
            {OPTIONALE_KRITERIEN.map((k) => (
              <KriteriumZeile key={k} k={k} wert={noten[k]} onChange={(v) => setzeNote(k, v)} />
            ))}
          </div>
        </details>
      </section>

      {/* Schritt 3: Anrichten */}
      <section className="karte" style={{ marginBottom: '1.5rem' }}>
        <h2>3 · Anrichten</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.6rem' }}>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Besuchsdatum</label>
              <input type="date" value={besuchsdatum} max={HEUTE} onChange={(e) => setBesuchsdatum(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Portionspreis (€)</label>
              <input type="number" step="0.10" value={preis} onChange={(e) => setPreis(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Wartezeit (Min.)</label>
              <input type="number" value={wartezeit} onChange={(e) => setWartezeit(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 8rem' }}>
              <label>Serviert als</label>
              <select value={serviertAls} onChange={(e) => setServiertAls(e.target.value)}>
                <option value="">–</option>
                <option value="TELLER">Teller</option>
                <option value="GLAS">Glas</option>
                <option value="BECHER">Becher</option>
                <option value="TO_GO">to go</option>
              </select>
            </div>
            <div style={{ flex: '1 1 12rem' }}>
              <label>Variante</label>
              <input value={variante} onChange={(e) => setVariante(e.target.value)} placeholder="klassisch mit Erdbeersauce" />
            </div>
          </div>
          <div>
            <label>Foto der Portion (optional, max. 5 MB)</label>
            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
              <input
                ref={kameraInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={(e) => fotoGewaehlt(e.target.files?.[0] ?? null)}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn btn-sekundaer"
                onClick={() => kameraInputRef.current?.click()}
              >
                Foto aufnehmen
              </button>
              <input
                ref={dateiInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => fotoGewaehlt(e.target.files?.[0] ?? null)}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="btn btn-sekundaer"
                onClick={() => dateiInputRef.current?.click()}
              >
                Aus Fotos wählen
              </button>
            </div>
            {fotoVorschau ? (
              <img
                src={fotoVorschau}
                alt="Vorschau"
                style={{ marginTop: '0.5rem', maxWidth: '100%', borderRadius: 'var(--radius)' }}
              />
            ) : null}
          </div>
          <div>
            <label>Verkostungsnotiz (optional)</label>
            <textarea value={fazit} onChange={(e) => setFazit(e.target.value)} />
          </div>
          <div>
            <label>Wiederkommen?</label>
            <select value={wiederkommen} onChange={(e) => setWiederkommen(e.target.value)}>
              <option value="">–</option>
              <option value="JA">ja</option>
              <option value="VIELLEICHT">vielleicht</option>
              <option value="NEIN">nein</option>
            </select>
          </div>
        </div>
      </section>

      {fehler ? <p className="hinweis" style={{ borderColor: 'var(--erdbeere)' }}>{fehler}</p> : null}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button className="btn btn-sekundaer" disabled={busy} onClick={() => absenden(false)}>
          Als Entwurf sichern
        </button>
        <button className="btn btn-primaer" disabled={busy || !pflichtOk} onClick={() => absenden(true)}>
          Veröffentlichen
        </button>
        <span style={{ color: 'var(--espresso-70)', fontSize: '0.85rem', alignSelf: 'center' }}>
          Nach dem Veröffentlichen kannst du die Verkostung nicht mehr ändern.
        </span>
      </div>
    </div>
  );
}
